import React from 'react';

import { captureException } from '@sentry/browser';
import { translate } from '~l10n/client';

import { SmallButton } from '~shared/components/buttons';
import { Dialog } from '~shared/components/Dialog';
import { Divider } from '~shared/components/Divider';
import { Inline } from '~shared/components/Inline';
import TextInput from '~shared/components/TextInput';
import { toast } from '~shared/components/Toast';
import {
  TeacherRouter,
  useSocrativeRouter,
} from '~teacher/context/RouterContext';
import { useGetWorkspaceDetail } from '~teacher/hook/workspaces';
import { useDeleteWorkspaceMutation } from '~teacher/hook/workspaces/useDeleteWorkspaceMutation';

import { isAxiosError } from '../../../../api/isAxiosError';

interface RenameProps {
  workspaceId: string;
  isOpen: boolean;
  setIsOpen: (newOpenState: boolean) => void;
}

interface UseResetDialogParams {
  setIsDisabled: React.Dispatch<React.SetStateAction<boolean>>;
  setConfirmName: React.Dispatch<React.SetStateAction<string>>;
  isOpen: boolean;
}

function useResetDialog({
  setIsDisabled,
  setConfirmName,
  isOpen,
}: UseResetDialogParams) {
  React.useEffect(() => {
    if (isOpen) {
      setIsDisabled(false);
      setConfirmName('');
    }
  }, [setConfirmName, isOpen, setIsDisabled]);
}

function useHandleDelete({
  setIsDisabled,
  confirmName,
  workspaceId,
  workspaceActualName,
  router,
}: {
  setIsDisabled: React.Dispatch<React.SetStateAction<boolean>>;
  confirmName: string;
  workspaceId: string;
  workspaceActualName: string;
  router: TeacherRouter;
}) {
  const deleteWorkspaceMutation = useDeleteWorkspaceMutation();

  return React.useCallback(() => {
    setIsDisabled(true);
    if (confirmName !== workspaceActualName) return;

    setIsDisabled(true);
    deleteWorkspaceMutation.mutate(
      { workspaceId: workspaceId },
      {
        onSuccess: () => {
          toast.positive(translate('Successfully deleted'));
          router.navigate('quizzes', { replace: true, trigger: true });
        },
        onError: (error) => {
          setIsDisabled(true);
          toast.critical(
            translate(
              'An error occurred while trying to permanently delete the shared library.'
            )
          );
          if (!isAxiosError(error)) {
            captureException(error);
          } else {
            if (error.response.status >= 500) {
              captureException(error);
            }
          }
        },
      }
    );
  }, [
    setIsDisabled,
    confirmName,
    workspaceActualName,
    deleteWorkspaceMutation,
    workspaceId,
    router,
  ]);
}

function useHandleClose(setIsOpen: (newOpenState: boolean) => void) {
  return React.useCallback(() => {
    setIsOpen(false);
  }, [setIsOpen]);
}

function useConfirmNameChange(
  setConfirmName: React.Dispatch<React.SetStateAction<string>>
) {
  return React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => setConfirmName(e.target.value),
    [setConfirmName]
  );
}

export function DeleteWorkspaceDialog({
  workspaceId,
  isOpen,
  setIsOpen,
}: RenameProps) {
  const workspaceDetail = useGetWorkspaceDetail({
    workspaceId: workspaceId,
  });
  const router = useSocrativeRouter();
  const [confirmName, setConfirmName] = React.useState('');
  const [isDisabled, setIsDisabled] = React.useState(true);

  useResetDialog({ isOpen, setConfirmName, setIsDisabled });

  const handleClose = useHandleClose(setIsOpen);

  const handleDelete = useHandleDelete({
    setIsDisabled: setIsDisabled,
    confirmName: confirmName,
    workspaceId: workspaceDetail.data?.id,
    workspaceActualName: workspaceDetail.data.name,
    router: router,
  });

  const handleConfirmNameChange = useConfirmNameChange(setConfirmName);

  const confirmMatchesActualName =
    workspaceDetail.isSuccess && workspaceDetail.data.name === confirmName;

  return (
    <Dialog
      onClose={handleClose}
      id="delete-workspace-modal"
      open={isOpen}
      title={translate('Permanently Delete Library') + '?'}
      closeLabel={translate('Cancel')}
    >
      <div className="mt-5 mb-5">
        <Divider />
      </div>
      <p className={'text-base text-blue-600 leading-8'}>
        {translate(
          'This action cannot be undone. Deleting "{0}" will delete all associated folders, quizzes, SOC codes, Import URLs, and disband all members.'
        ).format(workspaceDetail.data?.name)}
      </p>
      <div className="mt-5 mb-5">
        <Divider />
      </div>
      <div className="text-base leading-8">
        <label htmlFor="library-name" className="text-blue-900">
          {translate('Enter the library name to confirm')}
        </label>
        <TextInput
          maxLength={255}
          id="library-name"
          placeholder={workspaceDetail.data?.name}
          value={confirmName}
          onChange={handleConfirmNameChange}
          className="mt-3"
        />
      </div>
      <div className="pt-8">
        <Inline align="right">
          <SmallButton isDisabled={false} onClick={handleClose}>
            {translate('Cancel')}
          </SmallButton>
          <SmallButton
            isDisabled={!confirmMatchesActualName || isDisabled}
            onClick={handleDelete}
            appearance="ghost-destructive"
            a11yLabel="delete-workspace-button"
          >
            {translate('Delete Forever')}
          </SmallButton>
        </Inline>
      </div>
    </Dialog>
  );
}
